import {
  fetchEmailPreviewSampleRequest,
  fetchEmailPreviewSamplesRequest,
} from '@/services/api/emailCenter';
import { getIntlTranslation } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import { Select } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getLocale, useIntl } from 'umi';

import styles from './SampleSourcePicker.less';

export type SampleSourceType = 'appointment' | 'lead';

export interface SampleSource {
  type: SampleSourceType;
  id: string;
  /** Customer name, or their email when the name is missing. */
  label: string;
  /** Muted second line: the service and branch, or the journey and lead state. */
  description: string;
  /** Appointment start, or when the lead came in. ISO, may be absent. */
  at: string | null;
}

interface SampleSourcePickerProps {
  /** Which language the record's dates are formatted in. */
  language?: string;
  /** The chosen record's send-time variables, as a flat dot-path map. */
  onApply: (values: Record<string, string>, source: SampleSource) => void;
  /** The selection was cleared — drop whatever the last record filled in. */
  onClear: () => void;
  className?: string;
}

const TYPES: SampleSourceType[] = ['appointment', 'lead'];

/** `appointment:9f1c…` — a Select option value has to be one string. */
function optionKey(source: SampleSource): string {
  return `${source.type}:${source.id}`;
}

function parseOptionKey(key: string): { type: SampleSourceType; id: string } {
  const separator = key.indexOf(':');
  return {
    type: key.slice(0, separator) as SampleSourceType,
    id: key.slice(separator + 1),
  };
}

function formatWhen(at: string | null): string {
  if (!at) return '';
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(getLocale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/**
 * Populate a preview's sample data from a real lead or appointment.
 *
 * Typing thirteen sample values by hand answers "does the template render";
 * picking last Tuesday's appointment answers "is this the mail we sent Frau
 * Müller", which is the question people actually open a preview with. The
 * variables come from the server's send-time builders, so the two previews are
 * the same render with different inputs — nothing here is saved.
 *
 * Both record types are offered in one dropdown as two groups, because a user
 * looking for "that booking yesterday" should not first have to declare which
 * table it lives in. Searching hits both.
 */
const SampleSourcePicker: React.FC<SampleSourcePickerProps> = ({
  language,
  onApply,
  onClear,
  className,
}) => {
  const intl = useIntl();

  const [search, setSearch] = useState('');
  const [sources, setSources] = useState<SampleSource[]>([]);
  const [listing, setListing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [selected, setSelected] = useState<SampleSource | null>(null);
  // Nothing is fetched until the dropdown is opened once: this reads real
  // customer records, and a preview the user opens to read the copy should not
  // pull them in the background.
  const [opened, setOpened] = useState(false);

  // Typed characters outrun the requests they trigger; only the newest answer
  // may paint. Same for the variable fetch below, where a stale answer would
  // silently populate the preview with the wrong customer.
  const listRequest = useRef(0);
  const applyRequest = useRef(0);

  useEffect(() => {
    if (!opened) return undefined;
    const term = search.trim();
    const generation = ++listRequest.current;
    setListing(true);

    const timer = window.setTimeout(() => {
      Promise.all(
        TYPES.map((type) =>
          fetchEmailPreviewSamplesRequest({
            query: { type, search: term || undefined, limit: 10 },
          })
            .then((result) => {
              let rows: SampleSource[] = [];
              processAPIResponseNow(
                result,
                {
                  onSuccess: (data: SampleSource[] | undefined) => {
                    rows = Array.isArray(data) ? data : [];
                  },
                  // Swallowed on purpose: without this the helper toasts, and a
                  // partner whose users lack the lead permission would get an
                  // error popup on every keystroke.
                  onError: () => {},
                },
                'data',
              );
              return rows;
            })
            // A missing lead or appointment permission 403s one half of the
            // dropdown; the other half is still worth showing.
            .catch(() => [] as SampleSource[]),
        ),
      ).then((byType) => {
        if (generation !== listRequest.current) return;
        setSources(byType.flat());
        setListing(false);
      });
    }, 300);

    return () => {
      window.clearTimeout(timer);
    };
  }, [search, opened]);

  const load = useCallback(
    (source: SampleSource) => {
      const generation = ++applyRequest.current;
      setApplying(true);
      fetchEmailPreviewSampleRequest({
        params: { type: source.type, id: source.id },
        query: { language },
      })
        .then((result) => {
          if (generation !== applyRequest.current) return;
          processAPIResponseNow(
            result,
            {
              onSuccess: (data: { variables?: Record<string, string> }) =>
                onApply(data?.variables ?? {}, source),
              defaultErrorMessage: getIntlTranslation(intl, {
                id: 'pages.emailCenter.preview.sampleSourceFailed',
                defaultMessage: 'Could not load this record’s data.',
              }),
            },
            'data',
          );
        })
        .finally(() => {
          if (generation === applyRequest.current) setApplying(false);
        });
    },
    [language, onApply, intl],
  );

  // A record's dates are formatted server-side, so switching the preview
  // language has to re-fetch them — otherwise the body reads Dutch and the
  // appointment date stays German.
  useEffect(() => {
    if (selected) load(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const options = useMemo(
    () =>
      TYPES.map((type) => ({
        label:
          type === 'appointment'
            ? getIntlTranslation(intl, {
                id: 'pages.emailCenter.preview.sampleSourceAppointments',
                defaultMessage: 'Appointments',
              })
            : getIntlTranslation(intl, {
                id: 'pages.emailCenter.preview.sampleSourceLeads',
                defaultMessage: 'Leads',
              }),
        options: sources
          .filter((source) => source.type === type)
          .map((source) => {
            const when = formatWhen(source.at);
            return {
              value: optionKey(source),
              // Plain-string label for keyboard/screen-reader use; the visible
              // rows come from `optionRender` below.
              label: source.label,
              source,
              when,
            };
          }),
      })).filter((group) => group.options.length > 0),
    [sources, intl],
  );

  const handleChange = useCallback(
    (key: string | undefined) => {
      if (!key) {
        applyRequest.current += 1;
        setSelected(null);
        onClear();
        return;
      }
      const { type, id } = parseOptionKey(key);
      const source =
        sources.find(
          (candidate) => candidate.type === type && candidate.id === id,
        ) ?? null;
      setSelected(source);
      if (source) load(source);
    },
    [sources, load, onClear],
  );

  return (
    <Select
      className={className ? `${styles.picker} ${className}` : styles.picker}
      size="small"
      allowClear
      showSearch
      // The server does the searching (name and email, across both types), so
      // antd must not also filter the answer it just received.
      filterOption={false}
      loading={listing || applying}
      value={selected ? optionKey(selected) : undefined}
      onSearch={setSearch}
      onChange={handleChange}
      onOpenChange={(open) => {
        if (open) setOpened(true);
      }}
      options={options}
      // The chosen record drops out of `options` as soon as the user types a new
      // search, and antd would then show the raw `type:id` value in the box.
      labelRender={() => selected?.label ?? ''}
      notFoundContent={
        listing
          ? getIntlTranslation(intl, {
              id: 'pages.emailCenter.preview.sampleSourceLoading',
              defaultMessage: 'Searching…',
            })
          : getIntlTranslation(intl, {
              id: 'pages.emailCenter.preview.sampleSourceEmpty',
              defaultMessage: 'No recent leads or appointments found.',
            })
      }
      placeholder={getIntlTranslation(intl, {
        id: 'pages.emailCenter.preview.sampleSourcePlaceholder',
        defaultMessage: 'Use a real lead or appointment',
      })}
      optionRender={(option) => {
        const source = (option.data as { source?: SampleSource }).source;
        const when = (option.data as { when?: string }).when;
        return (
          <div className={styles.option}>
            <span className={styles.optionLabel}>{source?.label}</span>
            <span className={styles.optionMeta}>
              {[when, source?.description].filter(Boolean).join(' · ')}
            </span>
          </div>
        );
      }}
    />
  );
};

export default React.memo(SampleSourcePicker);
